import React, { useState, useEffect, useMemo } from 'react';
import api, { publicApi } from '../config/api';
import { getProductFallbackImage } from '../utils/imageHelpers';

interface SmartImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  productName?: string;
  productCategory?: string;
  productSku?: string;
  customFallback?: React.ReactNode;
  enableRetry?: boolean;
  isPublic?: boolean;  // Flag to indicate if this is a public/anonymous view
}

const SmartImage: React.FC<SmartImageProps> = ({
  src,
  alt,
  className = '',
  style,
  productName,
  productCategory,
  productSku,
  customFallback,
  enableRetry = true,
  isPublic = false
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fallbackSrc, setFallbackSrc] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Memoize the fallback image path based on product details
  const fallbackImagePath = useMemo(() => {
    return getProductFallbackImage({
      name: productName || alt,
      category: productCategory,
      sku: productSku
    });
  }, [productName, alt, productCategory, productSku]);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      setFallbackSrc(fallbackImagePath);
      return;
    }

    let isCancelled = false;
    let currentImageUrl: string | null = null;

    const loadImage = async () => {
      const maxRetries = enableRetry ? 2 : 0;
      let attempts = 0;
      
      while (attempts <= maxRetries && !isCancelled) {
        try {
          const imageSource = src;

          console.log(`SmartImage: Attempt ${attempts + 1} loading image:`, imageSource);

          // Use publicApi for anonymous users (no authentication), otherwise use regular api
          const hasAuth = typeof window !== 'undefined' && localStorage.getItem('access_token');
          const apiInstance = (isPublic || !hasAuth) ? publicApi : api;

          // Make request to get the image (authenticated or public)
          const response = await apiInstance.get(imageSource, {
            responseType: 'blob',
            timeout: 10000 // 10 second timeout
          });
          
          if (!isCancelled) {
            // Create a local URL for the blob
            const url = URL.createObjectURL(response.data);
            currentImageUrl = url;
            setImageUrl(url);
            setError(false);
            setRetryCount(attempts);
            setLoading(false);
            return; // Success, exit the retry loop
          }
        } catch (err: any) {
          console.error(`SmartImage: Attempt ${attempts + 1} failed for image:`, src, err);
          
          // If this is the last attempt, set error state
          if (attempts >= maxRetries) {
            if (!isCancelled) {
              setError(true);
              setFallbackSrc(fallbackImagePath);
              setRetryCount(attempts);
            }
          }
        }
        
        attempts++;
        
        // Add a small delay between retries
        if (attempts <= maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (!isCancelled) {
        setLoading(false);
      }
    };

    // Reset states
    setLoading(true);
    setError(false);
    setImageUrl(null);
    setRetryCount(0);
    
    loadImage();

    // Cleanup function
    return () => {
      isCancelled = true;
      if (currentImageUrl) {
        URL.revokeObjectURL(currentImageUrl);
      }
    };
  }, [src, fallbackImagePath, productSku, enableRetry]);

  // If custom fallback is provided and we have an error or no image
  if (customFallback && (error || (!imageUrl && !fallbackSrc))) {
    return <>{customFallback}</>;
  }

  // If loading, show a loading placeholder
  if (loading && !fallbackSrc) {
    return (
      <div className={`${className} smart-image-loading`}>
        <div className="spinner-border spinner-border-sm text-muted" role="status">
          <span className="visually-hidden">
            {retryCount > 0 ? `Loading... (Retry ${retryCount + 1})` : 'Loading...'}
          </span>
        </div>
        {retryCount > 0 && (
          <div className="smart-image-retry-info">
            Retry {retryCount + 1}
          </div>
        )}
      </div>
    );
  }

  // If we have an error or no image URL, use the smart fallback
  if (error || !imageUrl) {
    return (
      <img
        src={fallbackSrc || fallbackImagePath}
        alt={alt}
        className={`${className} smart-image-fallback`}
        style={style}
        onError={(e) => {
          // If even the fallback fails, use the most generic placeholder
          const target = e.target as HTMLImageElement;
          target.src = '/images/defaults/product.svg';
        }}
      />
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      style={style}
      onError={() => {
        setError(true);
        setFallbackSrc(fallbackImagePath);
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }
      }}
    />
  );
};

export default SmartImage;

// Add custom styles
const styles = `
.smart-image-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

.smart-image-fallback {
  opacity: 0.8;
}

.smart-image-retry-info {
  font-size: 10px;
  color: #6c757d;
  margin-top: 4px;
}
`;

// Create and append style element
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}